import { useAppRouter } from "./router.mjs";
import {
  ProductDocument,
  ProductMinimalInfoDocument,
  ViewProductPageDocument
} from "../graphql/Product.mjs";
import { sortByPositionAsc, toNonNullable } from "../utils/collections.mjs";
import { resolveProductPrice, resolveProductStock } from "../utils/product.mjs";
import { isAfter, isBefore } from "date-fns";
import { useMutation, useQuery } from "villus";
import { useState } from "#app";
export async function useProduct(slug) {
  const storedProduct = useState(
    `product-${slug}`,
    () => null
  );
  const { redirect: redirectToError } = useAppRouter();
  const { execute } = useQuery({
    query: ProductDocument,
    variables: { slug },
    fetchOnMount: false
  });
  await (async () => {
    const { data, error } = await execute();
    if (error) {
      redirectToError("/error", {
        query: {
          message: error.message
        }
      });
      return null;
    }
    if (!data?.product) {
      redirectToError("/error", {
        query: {
          message: "Product not found"
        }
      });
      return null;
    }
    storedProduct.value = mapProduct(data?.product);
    return storedProduct.value;
  })();
  return {
    product: storedProduct
  };
}
const FetchProductBySlugOptions = {
  query: ProductDocument,
  mapProduct
};
export const fetchProductBySlugFactory = (options = FetchProductBySlugOptions) => async function useFetchProductBySlug(slug) {
  const { query } = {
    ...FetchProductBySlugOptions,
    ...options
  };
  const extendedMappedProduct = (product) => {
    if (!product) {
      return null;
    }
    if (options.mapProduct !== mapProduct) {
      return mapProduct(product, [options.mapProduct]);
    }
    return mapProduct(product);
  };
  const { execute } = useQuery({
    query,
    variables: { slug },
    fetchOnMount: false
  });
  const { data, error } = await execute();
  if (error) {
    return null;
  }
  if (!data?.product) {
    return null;
  }
  return extendedMappedProduct(data?.product);
};
export function useVisitedProduct() {
  const { execute } = useMutation(ViewProductPageDocument);
  async function onVisitProduct(productId) {
    return await execute({
      product_id: productId
    });
  }
  return {
    onVisitProduct
  };
}
export async function useProductMinimal(slug) {
  const { execute } = useQuery({
    query: ProductMinimalInfoDocument,
    variables: {
      slug
    },
    fetchOnMount: false
  });
  return (await execute())?.data?.product || null;
}
function mapProduct(apiProduct, extraMappers) {
  if (!apiProduct) {
    throw new Error("Cannot map an empty product");
  }
  const mediaGallery = apiProduct?.media_gallery?.filter((i) => i?.url) || void 0;
  if (!mediaGallery?.length && apiProduct.image) {
    mediaGallery?.unshift({
      url: apiProduct.image.url,
      label: apiProduct.image.label
    });
  }
  const isNew = apiProduct.attributes?.find((attribute) => attribute?.key === "is_new")?.value === "Yes";
  const currentPrice = resolveProductPrice(apiProduct);
  const oldPrice = apiProduct.price_range.maximum_price?.regular_price.value ?? currentPrice;
  const dynamicPrice = (apiProduct.__typename === "BundleProduct" && apiProduct.dynamic_price) ?? false;
  const configurableGiftBoxOptionId = apiProduct.__typename === "ConfigurableProduct" ? apiProduct.options?.find(
    (option) => option?.__typename === "CustomizableFieldOption"
  )?.configurableItemId : null;
  const simpleGiftBoxOptionId = apiProduct.__typename === "SimpleProduct" ? apiProduct.options?.find(
    (option) => option?.__typename === "CustomizableFieldOption"
  )?.simpleItemUid : null;
  const deliveryCondition = apiProduct.attributes?.find(
    (attribute) => attribute?.key === "delivery_condition"
  )?.value ?? "";
  const product = {
    // ...apiProduct,
    isNew,
    id: apiProduct.id || null,
    stock: resolveProductStock(apiProduct) || null,
    price: resolveProductPrice(apiProduct),
    priceBefore: currentPrice < oldPrice ? oldPrice : void 0,
    media_gallery: mediaGallery ?? [],
    special_from_date: void 0,
    special_to_date: void 0,
    special_price: void 0,
    price_range: void 0,
    rating_summary: apiProduct.rating_summary,
    review_count: apiProduct.review_count,
    thumbnail: apiProduct.thumbnail ?? { url: "", label: "" },
    sku: apiProduct.sku ?? "",
    image: apiProduct.image ?? { url: "", label: "" },
    categories: apiProduct.categories ?? [],
    attributes: apiProduct.attributes ?? [],
    short_description: apiProduct.short_description,
    meta_title: apiProduct.meta_title,
    meta_description: apiProduct.meta_description,
    meta_keywords: apiProduct.meta_keywords,
    brand: apiProduct.brand,
    url_key: apiProduct.url_key,
    name: apiProduct.name,
    description: apiProduct.description,
    type: apiProduct.__typename,
    variants: void 0,
    groupItems: void 0,
    bundleItems: void 0,
    mainCategory: apiProduct.categories?.[0]?.name || "",
    configurationOptions: void 0,
    dynamicPrice,
    cartControl: {
      max: Number(apiProduct.cart_control.max_amount) ?? Number.MAX_VALUE,
      // maximum quantity allowed in the cart
      min: Number(apiProduct.cart_control.min_amount) ?? 0,
      // minimum quantity allowed in the cart
      step: Number(apiProduct.cart_control.increment_step) ?? 1,
      // quantity step
      unit: apiProduct.cart_control.unit ?? ""
      // quantity step
    },
    configurableGiftBoxOptionId,
    simpleGiftBoxOptionId,
    heroImage: apiProduct.hero_image,
    deliveryCondition,
    // inject extra mappers
    ...extraMappers?.reduce((acc, mapper) => {
      return {
        ...acc,
        ...mapper(apiProduct)
      };
    }, {}) ?? {}
  };
  if (apiProduct.__typename === "ConfigurableProduct" && apiProduct.variants?.length) {
    product.variants = toNonNullable(apiProduct.variants).map(
      (variant) => mapProductVariant(variant, product)
    );
    product.configurationOptions = apiProduct?.configurable_options;
  }
  if (apiProduct.__typename === "GroupedProduct" && apiProduct.items?.length) {
    product.groupItems = toNonNullable(
      apiProduct.items.sort(sortByPositionAsc)
    ).map(mapProductGroupItem);
  }
  if (apiProduct.__typename === "BundleProduct" && apiProduct.items?.length) {
    product.bundleItems = toNonNullable(
      apiProduct.items.sort(sortByPositionAsc)
    ).map(mapBundleProductItem);
  }
  delete product.special_from_date;
  delete product.special_from_date;
  delete product.special_price;
  delete product.price_range;
  return product;
}
function mapProductVariant(apiProduct, parent) {
  const newFrom = new Date(apiProduct?.product?.new_from_date || 0);
  const newTo = new Date(apiProduct?.product?.new_to_date || 0);
  const now = /* @__PURE__ */ new Date();
  const isNew = isAfter(newTo, now) && isBefore(newFrom, now);
  const currentPrice = apiProduct?.product && resolveProductPrice(apiProduct?.product) || 0;
  const oldPrice = apiProduct?.product?.price_range.maximum_price?.regular_price.value ?? currentPrice;
  const mediaGallery = apiProduct?.product?.media_gallery?.filter((i) => i?.url) || [];
  if (!mediaGallery.length && apiProduct?.product?.image) {
    mediaGallery.unshift({
      url: apiProduct?.product?.image.url,
      label: apiProduct?.product.image.label
    });
  }
  const product = {
    id: apiProduct?.product?.id,
    name: apiProduct?.product?.name,
    type: apiProduct?.product?.__typename,
    isNew,
    stock: apiProduct?.product && resolveProductStock(apiProduct?.product) || null,
    price: apiProduct?.product && resolveProductPrice(apiProduct?.product) || 0,
    parentSku: parent.sku,
    parentSlug: parent.url_key,
    slug: apiProduct?.product?.url_key,
    sku: apiProduct?.product?.sku,
    priceBefore: currentPrice < oldPrice ? oldPrice : void 0,
    media_gallery: mediaGallery,
    special_from_date: void 0,
    special_to_date: void 0,
    special_price: void 0,
    price_range: void 0,
    thumb: apiProduct?.product?.image,
    attributes: apiProduct?.attributes
  };
  return product;
}
function mapProductGroupItem(item) {
  return {
    id: item.product?.id,
    qty: item.qty || 0,
    name: item.product?.name,
    stock: item.product ? resolveProductStock(item.product) : 0,
    price: item.product ? resolveProductPrice(item.product) : 0,
    sku: item.product?.sku,
    thumb: item.product?.thumbnail
  };
}
function mapBundleProductItem(item) {
  return {
    id: item.option_id,
    label: item.title,
    isRequired: !!item.required,
    options: toNonNullable(item.options).map((option) => {
      return {
        id: option?.uid,
        isDefault: !!option.is_default,
        canChangeQty: !!option.can_change_quantity,
        label: option?.label,
        price: option.product ? resolveProductPrice(option.product) : 0,
        thumbnail: option.product?.thumbnail,
        qty: option.quantity ?? 0,
        stock: option.product?.only_x_left_in_stock ?? 0
      };
    })
  };
}
